import { useState } from 'react';
import { Trash2, Download, FileText } from 'lucide-react';
import { api } from '../lib/api';
import Lightbox from './Lightbox';
import type { ReportAttachment } from '../types';

interface Props {
  attachments: ReportAttachment[];
  onDeleted?: (id: number) => void;
}

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

export default function ReportAttachments({ attachments, onDeleted }: Props) {
  const [deleting, setDeleting] = useState<number | null>(null);
  const [viewIndex, setViewIndex] = useState<number | null>(null);

  const images = attachments
    .filter((att) => IMAGE_EXT.test(att.filename) || att.mimetype.startsWith('image/'))
    .map((att) => ({ url: att.url, label: att.filename }));

  const handleDelete = async (id: number) => {
    setDeleting(id);
    try {
      await api.delete(`/reports/attachments/${id}`);
      onDeleted?.(id);
    } catch {
      // ignore
    } finally {
      setDeleting(null);
    }
  };

  if (attachments.length === 0) return null;

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {attachments.map((att) => {
          const isImage = IMAGE_EXT.test(att.filename) || att.mimetype.startsWith('image/');
          if (isImage) {
            return (
              <div
                key={att.id}
                className="relative h-16 w-20 rounded-lg overflow-hidden border border-gray-200 group"
                title="Click to view fullscreen"
              >
                <button
                  type="button"
                  onClick={() => {
                    const idx = images.findIndex((img) => img.url === att.url);
                    if (idx >= 0) setViewIndex(idx);
                  }}
                  className="absolute inset-0 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <img src={att.url} alt={att.filename} className="h-full w-full object-cover" />
                  <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(att.id)}
                  disabled={deleting === att.id}
                  className="absolute top-1 right-1 p-1 rounded bg-black/50 text-white hover:bg-red-600 disabled:opacity-50"
                  title="Delete attachment"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
                {deleting === att.id && <span className="absolute inset-0 bg-white/60 flex items-center justify-center text-xs">...</span>}
              </div>
            );
          }
          return (
            <span
              key={att.id}
              className="inline-flex items-center px-3 py-1.5 rounded-lg border border-gray-200 text-xs bg-gray-50"
            >
              <FileText className="h-3.5 w-3.5 text-gray-400 mr-1.5" />
              <a href={att.url} target="_blank" rel="noreferrer" className="text-primary hover:text-primary-dark max-w-[180px] truncate">
                {att.filename}
              </a>
              <a
                href={att.url}
                download
                className="ml-1.5 p-1 rounded hover:bg-primary/10 text-primary"
                title="Download"
              >
                <Download className="h-3.5 w-3.5" />
              </a>
              <button
                onClick={() => handleDelete(att.id)}
                disabled={deleting === att.id}
                className="ml-0.5 p-1 rounded hover:bg-red-50 text-red-500 disabled:opacity-50"
                title="Delete attachment"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          );
        })}
      </div>

      {viewIndex !== null && images.length > 0 && (
        <Lightbox
          images={images}
          index={Math.min(viewIndex, images.length - 1)}
          onClose={() => setViewIndex(null)}
          onNavigate={(idx) => setViewIndex(idx)}
        />
      )}    </div>
  );
}
